/**
 * AuthCallback — handles the Google OAuth 2.0 Authorization Code redirect.
 * Google redirects here as: /auth/google/callback?code=xxx&state=xxx
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH — always derive the redirect from window.location.origin.
 *
 * This component:
 *  1. Reads `code` from URL query params.
 *  2. POSTs { code, redirect_uri } to the backend for exchange — the
 *     backend sets httpOnly session cookies on success.
 *  3. Caches the returned (non-sensitive) user object for optimistic UI.
 *  4. Navigates to /home.
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { CustomerStorage, AdminStorage, GuestIntent, hadPriorCustomerSession, getPostLoginRedirect } from '../../utils/storage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setGoogleUser } = useAuth();

  // useRef prevents double-fire under React StrictMode
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const exchangeCode = async () => {
      // Read Google's authorization code from URL query params: ?code=xxx
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const returnedState = params.get('state');
      const error = params.get('error');

      // SECURITY (CSRF): the `state` Google echoes back MUST match the one
      // this tab generated before redirecting (see AuthPage.handleGoogleSignIn).
      // One-time use — removed immediately regardless of outcome.
      const expectedState = sessionStorage.getItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_state');

      if (error || !code) {
        // User denied access or something went wrong
        navigate('/auth', { replace: true });
        return;
      }

      if (!returnedState || !expectedState || returnedState !== expectedState) {
        // Mismatched/missing state — this callback wasn't triggered by a
        // login this browser tab actually started. Reject without ever
        // contacting the backend token exchange.
        navigate('/auth', { replace: true });
        return;
      }

      // The redirect_uri sent here MUST exactly match what was used in
      // the authorization request — derived from window.location.origin.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
      // THIS BREAKS THE AUTH — always derive the redirect from window.location.origin.
      const redirectUri = `${window.location.origin}/auth/google/callback`;

      // Clear callback params from URL immediately (clean address bar)
      window.history.replaceState(null, '', '/auth/google/callback');

      try {
        const response = await axios.post(`${API}/auth/google/callback`, {
          code,
          redirect_uri: redirectUri,
        }, { withCredentials: true });

        const { user } = response.data;

        // Snapshot BEFORE clearAuthOnly() wipes it — see AuthContext.login().
        const priorSession = hadPriorCustomerSession();

        // Clear stale auth (preserves bookingIntent like normal login)
        CustomerStorage.clearAuthOnly();

        // SECURITY: cross-user leak guard — if a different user was
        // already signed in on this device, their leftover GuestIntent
        // pick must not carry over to this incoming Google account.
        if (priorSession) {
          GuestIntent.clear();
        }

        AdminStorage.clear();

        // Server already set the httpOnly session cookies — cache only
        // the non-sensitive display object.
        localStorage.setItem('user', JSON.stringify(user));

        // Sync AuthContext
        setGoogleUser(user);

        // If the Google account has no phone number yet, collect it before
        // allowing access to the app (phone is required for bookings).
        if (!user.phone) {
          navigate('/auth/google/complete-profile', { replace: true });
        } else {
          navigate(getPostLoginRedirect(), { replace: true });
        }
      } catch {
        navigate('/auth', { replace: true });
      }
    };

    exchangeCode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // No visible UI — redirect happens immediately
}
